<template>
  <div class="home-container">
    <el-row :gutter="10">
      <!-- Left Column -->
      <el-col :span="18">
        <!-- Real-time Stats -->
        <el-card class="box-card real-time-stats-card">
          <div slot="header" class="clearfix">
            <span class="card-title">实时概况</span>
          </div>
          <div class="grid-container stats-grid">
            <div v-for="(stat, index) in realTimeStats" :key="index" class="grid-item">
              <div class="stat-header">
                <span class="stat-title">{{ stat.title }}</span>
                <span class="stat-yesterday">昨日{{ stat.yesterday }}</span>
              </div>
              <p class="stat-value">{{ stat.value }}</p>
              <p class="stat-comparison">较昨日 <span :class="stat.change > 0 ? 'positive' : 'negative'">
                <img :src="stat.change > 0 ? require('@/assets/home/up.png') : require('@/assets/home/down.png')" class="trend-icon" /> {{ Math.abs(stat.change).toFixed(2) }}%
              </span></p>
            </div>
          </div>
        </el-card>

        <!-- Todo Items -->
        <el-card class="box-card todo-card" style="margin-top: 10px;">
          <div slot="header" class="clearfix">
            <span class="card-title">代办事项</span>
          </div>
          <div class="grid-container todo-grid">
            <div v-for="(item, index) in todoItems" :key="index" class="todo-item">
              <p class="todo-value">{{ item.value }}</p>
              <p class="todo-title">{{ item.title }}</p>
            </div>
          </div>
        </el-card>
      </el-col>

      <!-- Right Column -->
      <el-col :span="6">
        <!-- Common Functions -->
        <el-card class="box-card common-functions-card">
          <div slot="header" class="clearfix">
            <span class="card-title">常用功能</span>
          </div>
          <div class="grid-container functions-grid">
            <div v-for="(func, index) in commonFunctions" :key="index" class="function-item" @click="navigateTo(func)">
              <img :src="func.icon" :alt="func.title">
              <p>{{ func.title }}</p>
            </div>
          </div>
        </el-card>

        <!-- Device Stats -->
        <el-card class="box-card device-stats-card" style="margin-top: 10px;">
          <div slot="header" class="clearfix">
            <span class="card-title">设备情况</span>
          </div>
          <div class="device-stats-container">
            <div v-for="(stat, index) in deviceStats" :key="index" class="device-stat-item">
              <p class="device-stat-value" :class="getDeviceStatColor(index)">{{ stat.value }}</p>
              <p class="device-stat-title">{{ stat.title }}</p>
            </div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Charts Row -->
    <el-row :gutter="10" style="margin-top: 10px;">
      <el-col :span="12">
        <el-card class="box-card chart-card">
          <div slot="header" class="clearfix card-header-flex">
            <span class="card-title">聚合数据概览</span>
            <div class="header-tools">
              <el-select v-model="aggChartType" size="mini" style="width: 120px; margin-right: 10px;">
                <el-option label="投递重量" value="deliver_weight"></el-option>
                <el-option label="投递次数" value="deliver_times"></el-option>
                <el-option label="清运重量" value="cleaning_weight"></el-option>
                <el-option label="清运次数" value="cleaning_times"></el-option>
              </el-select>
              <el-button-group class="custom-btn-group">
                <el-button size="mini" :type="aggDays === 7 ? 'success' : 'default'" @click="aggDays = 7" round>7天</el-button>
                <el-button size="mini" :type="aggDays === 15 ? 'success' : 'default'" @click="aggDays = 15" round>15天</el-button>
                <el-button size="mini" :type="aggDays === 30 ? 'success' : 'default'" @click="aggDays = 30" round>30天</el-button>
              </el-button-group>
            </div>
          </div>
          <div ref="aggChart" class="chart agg-chart"></div>
        </el-card>
      </el-col>
      <el-col :span="12">
        <el-card class="box-card chart-card">
          <div slot="header" class="clearfix card-header-flex">
            <span class="card-title">聚合排行榜</span>
            <div class="header-tools">
              <el-select v-model="rankChartType" size="mini" style="width: 120px; margin-right: 10px;">
                <el-option label="投递重量" value="user_deliver_weight"></el-option>
                <el-option label="投递次数" value="user_deliver_times"></el-option>
                <el-option label="回收箱投递量" value="device_recycle_weight"></el-option>
                <el-option label="回收箱清运量" value="device_cleaning_weight"></el-option>
              </el-select>
              <el-date-picker v-model="rankDateRange" type="daterange" size="mini" range-separator="至" start-placeholder="开始日期" end-placeholder="结束日期" style="width: 220px;"></el-date-picker>
            </div>
          </div>
          <div ref="rankChart" class="chart rank-chart"></div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getCompanySummary, getDevicesStats, getOverviewStats, getRankingsStats } from '@/api/statistics'

export default {
  name: 'Home',
  data() {
    return {
      updateTime: new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' }),
      aggChartType: 'deliver_weight',
      aggDays: 7,
      rankChartType: 'user_deliver_weight',
      rankDateRange: [],
      realTimeStats: [
        { title: '今日总投递 (KG)', value: '0', yesterday: '0', change: 0 },
        { title: '今日投递次数 (次)', value: '0', yesterday: '0', change: 0 },
        { title: '今日产生环保金 (元)', value: '0', yesterday: '0', change: 0 },
        { title: '清运包数', value: '0', yesterday: '0', change: 0 },
        { title: '今日提现环保金 (元)', value: '0', yesterday: '0', change: 0 },
        { title: '今日参与会员', value: '0', yesterday: '0', change: 0 },
        { title: '今日新增会员', value: '0', yesterday: '0', change: 0 },
        { title: '清运重量 (KG)', value: '0', yesterday: '0', change: 0 },
      ],
      todoItems: [
        { title: '待审核提现', value: 0 },
        { title: '待审核订单', value: 0 },
        { title: '预约上门', value: 0 },
        { title: '满溢报警', value: 0 },
        { title: '离线报警', value: 0 },
        { title: '5公斤以上', value: 0 },
        { title: '异常订单', value: 0 },
        { title: '箱门上报', value: 0 },
        { title: '周上报申请', value: 0 },
      ],
      deviceStats: [
        { title: '设备总数', value: 323 },
        { title: '在线设备', value: 270 },
        { title: '离线设备', value: 53 },
      ],
      commonFunctions: [
        { title: '投递订单', icon: require('@/assets/home/tddd.png'), route: '/deliveries' },
        { title: '清溢警报', icon: require('@/assets/home/mybj.png'), route: '/devices', query: { hardware_status: '1' } },
        { title: '用户列表', icon: require('@/assets/home/hylb.png'), route: '/users/list' },
        { title: '员工管理', icon: require('@/assets/home/tggl.png'), route: '/users/list', query: { tab: 'staff' } },
        { title: '数据状况', icon: require('@/assets/home/sjgk.png'), route: '/asset-device-delivery-stats' },
        { title: '程序装修', icon: require('@/assets/home/cxzx.png'), route: '/system-apps' },
        { title: '清运袋', icon: require('@/assets/home/qyd.png') },
        { title: '清运列表', icon: require('@/assets/home/qylb.png'), route: '/clearance' },
        { title: '用户提现', icon: require('@/assets/home/yhtx.png'), route: '/finance/withdraw-records' },
      ],
    }
  },
  mounted() {
    this.initAggChart();
    this.initRankChart();
    this.fetchRealTimeSummary();
    this.fetchDeviceStats();
    this.fetchAggOverview();
    this.timer = setInterval(() => {
      this.updateTime = new Date().toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
    }, 60000);
  },
  watch: {
    rankChartType() { this.fetchRankings() },
    rankDateRange() { this.fetchRankings() },
    aggChartType() { this.fetchAggOverview() },
    aggDays() { this.fetchAggOverview() }
  },
  beforeDestroy() {
    clearInterval(this.timer);
  },
  methods: {
    getCompanyId() {
      const fromStore = (this.$store && this.$store.state && this.$store.state.auth && this.$store.state.auth.userInfo && this.$store.state.auth.userInfo.company_id) || ''
      const fromLocal = localStorage.getItem('company_id') || ''
      return fromStore || fromLocal || ''
    },
    navigateTo(func) {
      const path = func && func.route
      const query = (func && func.query) || {}
      if (path) {
        this.$router.push({ path, query })
        return
      }
      this.$message.info('此功能暂未开放')
    },
    formatNumber(n) {
      if (n === null || n === undefined || n === '') return '0'
      const num = Number(n)
      if (Number.isNaN(num)) return String(n)
      return String(Math.trunc(num))
    },
    calcChange(today, yesterday) {
      const t = Number(today)
      const y = Number(yesterday)
      if (Number.isNaN(t) || Number.isNaN(y)) return 0
      if (y === 0) {
        return t === 0 ? 0 : (t > 0 ? 100 : -100)
      }
      return ((t - y) / y) * 100
    },
    pick(obj, keys) {
      for (let i = 0; i < keys.length; i++) {
        const k = keys[i]
        if (obj && obj[k] !== undefined && obj[k] !== null) return obj[k]
      }
      return null
    },
    getDeviceStatColor(index) {
      if (index === 0) return 'text-primary'
      if (index === 1) return 'text-success'
      if (index === 2) return 'text-danger'
      return ''
    },
    async fetchRealTimeSummary() {
      try {
        const companyId = this.getCompanyId()
        if (!companyId) return
        const res = await getCompanySummary({ company_id: companyId })
        const body = res && res.data ? res.data : res
        const todayWeight = body.today_delivery_weight
        const yesterdayWeight = body.yesterday_delivery_weight
        const todayCount = body.today_delivery_count
        const yesterdayCount = body.yesterday_delivery_count
        const todayMoney = body.today_recycle_amount
        const yesterdayMoney = body.yesterday_recycle_amount
        const bagCount = body.today_cleared_count
        const bagYesterday = body.yesterday_cleared_count
        const todayWithdraw = body.today_withdraw_amount
        const yesterdayWithdraw = body.yesterday_withdraw_amount
        const todayMembers = body.today_unique_users
        const yesterdayMembers = body.yesterday_unique_users
        const todayNewMembers = body.today_new_registrations
        const yesterdayNewMembers = body.yesterday_new_registrations
        const todayClearWeight = body.today_cleared_weight
        const yesterdayClearWeight = body.yesterday_cleared_weight
        this.realTimeStats = [
          { title: '今日总投递 (KG)', value: this.formatNumber(todayWeight), yesterday: this.formatNumber(yesterdayWeight), change: this.calcChange(todayWeight, yesterdayWeight) },
          { title: '今日投递次数 (次)', value: this.formatNumber(todayCount), yesterday: this.formatNumber(yesterdayCount), change: this.calcChange(todayCount, yesterdayCount) },
          { title: '今日产生环保金 (元)', value: this.formatNumber(todayMoney), yesterday: this.formatNumber(yesterdayMoney), change: this.calcChange(todayMoney, yesterdayMoney) },
          { title: '清运包数', value: this.formatNumber(bagCount), yesterday: this.formatNumber(bagYesterday), change: this.calcChange(bagCount, bagYesterday) },
          { title: '今日提现环保金 (元)', value: this.formatNumber(todayWithdraw), yesterday: this.formatNumber(yesterdayWithdraw), change: this.calcChange(todayWithdraw, yesterdayWithdraw) },
          { title: '今日参与会员', value: this.formatNumber(todayMembers), yesterday: this.formatNumber(yesterdayMembers), change: this.calcChange(todayMembers, yesterdayMembers) },
          { title: '今日新增会员', value: this.formatNumber(todayNewMembers), yesterday: this.formatNumber(yesterdayNewMembers), change: this.calcChange(todayNewMembers, yesterdayNewMembers) },
          { title: '清运重量 (KG)', value: this.formatNumber(todayClearWeight), yesterday: this.formatNumber(yesterdayClearWeight), change: this.calcChange(todayClearWeight, yesterdayClearWeight) }
        ]
      } catch (e) {
        console.error('fetchRealTimeSummary error', e)
      }
    },
    async fetchDeviceStats() {
      try {
        const companyId = this.getCompanyId()
        const res = await getDevicesStats({ company_id: companyId })
        const body = res && res.data ? res.data : res
        const total = body.total_devices
        const online = body.online_devices
        const offline = body.offline_devices
        const overflow = body.overflow_alerts
        const abnormal = body.abnormal_devices != null ? body.abnormal_devices : 0
        this.deviceStats = [
          { title: '设备总数', value: this.formatNumber(total) },
          { title: '在线设备', value: this.formatNumber(online) },
          { title: '离线设备', value: this.formatNumber(offline) }
        ]
        this.todoItems = this.todoItems.map(i => {
          if (i.title === '待审核提现') return { title: i.title, value: 0 }
          if (i.title === '待审核订单') return { title: i.title, value: 0 }
          if (i.title === '预约上门') return { title: i.title, value: 0 }
          if (i.title === '满溢报警') return { title: i.title, value: this.formatNumber(overflow) }
          if (i.title === '离线报警') return { title: i.title, value: this.formatNumber(offline) }
          if (i.title === '5公斤以上') return { title: '异常报警', value: this.formatNumber(abnormal) }
          return i
        })
      } catch (e) {
        console.error('fetchDeviceStats error', e)
      }
    },
    initAggChart() {
      this._aggChart = echarts.init(this.$refs.aggChart)
    },
    async fetchAggOverview() {
      try {
        const companyId = this.getCompanyId()
        if (!companyId) return
        const res = await getOverviewStats({ company_id: companyId, range: this.aggDays })
        const body = res && res.data ? res.data : res
        const seriesKey = this.aggChartType
        const arr = Array.isArray(body[seriesKey]) ? body[seriesKey] : []
        const dates = arr.map(i => i.date)
        const values = arr.map(i => Math.trunc(Number(i.value || 0)))
        const colors = {
          deliver_weight: ['rgba(255, 110, 98, 0.5)', '#FF6E62'],
          deliver_times: ['rgba(64, 158, 255, 0.5)', '#409EFF'],
          cleaning_weight: ['rgba(103, 194, 58, 0.5)', '#67C23A'],
          cleaning_times: ['rgba(255, 196, 0, 0.5)', '#FFC400']
        }
        const [area, line] = colors[seriesKey] || colors.deliver_weight
        const option = {
          tooltip: { trigger: 'axis' },
          grid: { left: '2%', right: '3%', top: '5%', bottom: '10%', containLabel: true },
          xAxis: { type: 'category', boundaryGap: false, data: dates },
          yAxis: { type: 'value' },
          series: [{
            type: 'line',
            smooth: true,
            lineStyle: { width: 3, color: line },
            showSymbol: false,
            areaStyle: { color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [{ offset: 0, color: area }, { offset: 1, color: 'rgba(255, 255, 255, 0)' }]) },
            itemStyle: { color: line },
            data: values
          }]
        }
        this._aggChart.setOption(option)
      } catch (e) {
        console.error('fetchAggOverview error', e)
      }
    },
    initRankChart() {
      this._rankChart = echarts.init(this.$refs.rankChart)
      this.fetchRankings()
    },
    formatDateTime(d, endOfDay = false) {
      const pad = n => String(n).padStart(2, '0')
      const y = d.getFullYear(), m = pad(d.getMonth() + 1), day = pad(d.getDate())
      const hh = endOfDay ? '23' : pad(d.getHours())
      const mm = endOfDay ? '59' : pad(d.getMinutes())
      const ss = endOfDay ? '59' : pad(d.getSeconds())
      return `${y}-${m}-${day} ${hh}:${mm}:${ss}`
    },
    getDefaultRange() {
      const to = new Date()
      const from = new Date(to.getTime() - 6 * 24 * 60 * 60 * 1000)
      return { from: this.formatDateTime(from, false), to: this.formatDateTime(to, true) }
    },
    async fetchRankings() {
      try {
        const companyId = this.getCompanyId()
        if (!companyId) return
        let fromTo
        if (Array.isArray(this.rankDateRange) && this.rankDateRange.length === 2) {
          fromTo = {
            from: this.formatDateTime(new Date(this.rankDateRange[0]), false),
            to: this.formatDateTime(new Date(this.rankDateRange[1]), true)
          }
        } else {
          fromTo = this.getDefaultRange()
        }
        const res = await getRankingsStats({ company_id: companyId, from: fromTo.from, to: fromTo.to })
        const body = res && res.data ? res.data : res
        const key = this.rankChartType
        const arr = Array.isArray(body[key]) ? body[key] : []
        const names = arr.map(i => i.name)
        const values = arr.map(i => Math.trunc(Number(i.value || 0)))
        const option = {
          tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
          grid: { left: '2%', right: '3%', top: '5%', bottom: '10%', containLabel: true },
          xAxis: { type: 'category', data: names, axisLabel: { rotate: 30 } },
          yAxis: { type: 'value' },
          series: [{
            type: 'bar',
            barWidth: '20%',
            itemStyle: { borderRadius: [10, 10, 0, 0], color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [ { offset: 0, color: '#42d866' }, { offset: 1, color: '#28b652' } ]) },
            data: values
          }]
        }
        this._rankChart.setOption(option)
      } catch (e) {
        console.error('fetchRankings error', e)
      }
    }
  },
  
}
</script>

<style scoped>
.home-container { padding: 10px; background-color: #f4f6f8; }
.box-card { margin-bottom: 10px; border-radius: 8px; border: none; }
::v-deep .el-card__header { border-bottom: none; padding-bottom: 0; }
.clearfix:before, .clearfix:after { display: table; content: ""; }
.clearfix:after { clear: both; }
.card-header-flex { display: flex; justify-content: space-between; align-items: center; }
.el-card__header { font-weight: 600; }

.card-title {
  display: flex;
  align-items: center;
  font-weight: bold;
  font-size: 15px;
  color: #333;
}
.card-title::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 16px;
  background-color: #28b652;
  margin-right: 10px;
  border-radius: 2px;
}

/* Real-time Stats Card */
.real-time-stats-card .update-time { float: right; font-size: 12px; color: #909399; font-weight: 400; }
.stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.stats-grid .grid-item { background: #f8fafa; padding: 10px 12px; border-radius: 4px; }
.stat-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.stat-title { font-size: 13px; color: #606266; }
.stat-yesterday { font-size: 12px; color: #909399; }
.stat-value { font-size: 20px; font-weight: bold; margin: 0 0 4px; color: #303133; }
.stat-comparison { font-size: 12px; color: #909399; margin: 0; }
.stat-comparison .positive { color: #67c23a; }
.stat-comparison .negative { color: #f56c6c; }
.trend-icon {
  width: 12px;
  height: 12px;
  margin-right: 2px;
  vertical-align: middle;
}

/* Todo Card */
.todo-grid { display: grid; grid-template-columns: repeat(9, 1fr); gap: 10px; text-align: center; }
.todo-item { display: flex; flex-direction: column; align-items: center; justify-content: center; }
.todo-value { font-size: 20px; font-weight: bold; margin: 0 0 8px; color: #303133; }
.todo-title { font-size: 12px; color: #909399; margin: 0; }

/* Device Stats */
.device-stats-card .el-card__body { padding: 10px 20px; }
.device-stats-container { display: flex; justify-content: space-around; text-align: center; }
.device-stat-value { font-size: 22px; font-weight: bold; margin: 0; }
.text-primary { color: #409eff; }
.text-success { color: #67c23a; }
.text-danger { color: #f56c6c; }
.device-stat-title { font-size: 14px; color: #606266; margin: 5px 0 0; }

/* Common Functions */
.functions-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
.function-item { text-align: center; cursor: pointer; }
.function-item:hover { opacity: 0.8; }

.custom-btn-group {
  display: inline-flex;
}
.custom-btn-group .el-button {
  border: 1px solid transparent;
  background: #f4f6f8;
  color: #606266;
}
.custom-btn-group .el-button--success {
  background: #ffffff;
  border-color: #28b652;
  color: #28b652;
  box-shadow: 0 2px 4px rgba(40, 182, 82, 0.1);
}
.custom-btn-group .el-button:hover {
  color: #28b652;
}
.custom-btn-group .el-button--success:hover {
  background: #ffffff;
  border-color: #28b652;
  color: #28b652;
}

.function-item img { width: 28px; height: 28px; margin-bottom: 8px; }
.function-item p { font-size: 13px; margin: 0; color: #303133; }

/* Chart */
.chart-card { display: flex; flex-direction: column; }
.chart-card .el-card__body { flex-grow: 1; padding: 10px; }
.chart { width: 100%; height: 260px; }

.home-container > .el-row {
  display: flex;
  flex-wrap: wrap;
}
.home-container > .el-row > .el-col {
  display: flex;
  flex-direction: column;
}
.home-container .box-card {
  display: flex;
  flex-direction: column;
  width: 100%;
}
.home-container .el-card__body {
  flex-grow: 1;
}
.home-container > .el-row:last-of-type > .el-col:first-of-type > .common-functions-card {
    flex-grow: 1;
}
</style>
